import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Save, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import OwnerLayout from "@/components/owner/OwnerLayout";
import { useAuth } from "@/context/AuthContext";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

export default function OwnerProfilePage() {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/owner/profile")
      .then((r) => {
        const d = r.data || {};
        setName(d.name || "");
        setEmail(d.email || "");
        setPhone(d.phone || "");
        setContacts(d.whatsapp_contacts || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const addContact = () => setContacts((c) => [...c, { label: "", phone: "" }]);
  const removeContact = (i) => setContacts((c) => c.filter((_, idx) => idx !== i));
  const updateContact = (i, key, val) =>
    setContacts((c) => c.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));

  const save = async () => {
    for (const c of contacts) {
      if (!c.label.trim() || c.phone.replace(/[^0-9]/g, "").length < 8) {
        toast.error("Lengkapi label dan nomor WhatsApp yang valid pada setiap kontak.");
        return;
      }
    }
    setSaving(true);
    try {
      const { data } = await apiClient.put("/owner/profile", {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        avatar: user?.avatar || null,
        whatsapp_contacts: contacts.map((c) => ({ label: c.label.trim(), phone: c.phone.trim() })),
      });
      updateUser?.(data);
      setContacts(data.whatsapp_contacts || []);
      toast.success("Profil berhasil disimpan.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <OwnerLayout testid="owner-profile-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight mb-6">Profile</h1>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-primary" size={26} /></div>
      ) : (
        <div className="space-y-6 max-w-2xl">
          <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Nama</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="owner-profile-name"
                className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Email</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="owner-profile-email"
                  className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">No. Telepon</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="owner-profile-phone"
                  className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
            </div>
            <div className="flex justify-between gap-4 pt-1 text-sm text-muted-foreground">
              <span>Role: <span className="font-medium text-foreground">{user?.role}</span></span>
              <span>Status: <span className="font-medium text-foreground">{user?.status}</span></span>
            </div>
          </div>

          {/* WhatsApp contacts */}
          <div className="bg-card border border-border rounded-2xl p-6" data-testid="owner-profile-whatsapp">
            <div className="flex items-center gap-2 mb-1">
              <MessageCircle size={18} className="text-emerald-500" />
              <h2 className="font-heading text-lg font-semibold">Nomor WhatsApp</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Nomor ini ditampilkan di halaman detail kos, di samping tombol "Hubungi Owner". Anda bisa menambah lebih dari satu.
            </p>

            <div className="space-y-3">
              {contacts.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="owner-profile-whatsapp-empty">Belum ada nomor WhatsApp.</p>
              )}
              {contacts.map((c, i) => (
                <div key={i} className="flex flex-col sm:flex-row gap-2" data-testid={`owner-profile-wa-row-${i}`}>
                  <input value={c.label} onChange={(e) => updateContact(i, "label", e.target.value)}
                    placeholder="Label (mis. Admin Kos)" data-testid={`owner-profile-wa-label-${i}`}
                    className="sm:w-1/3 rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                  <input value={c.phone} onChange={(e) => updateContact(i, "phone", e.target.value)}
                    inputMode="tel" placeholder="Nomor (mis. 0812xxxxxxxx)" data-testid={`owner-profile-wa-phone-${i}`}
                    className="flex-1 rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                  <button onClick={() => removeContact(i)} data-testid={`owner-profile-wa-remove-${i}`}
                    className="grid place-items-center w-10 h-10 shrink-0 rounded-xl border border-rose-200 text-rose-600 hover:bg-rose-50 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>

            <button onClick={addContact} data-testid="owner-profile-wa-add"
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm font-semibold hover:bg-secondary/60 transition-colors">
              <Plus size={15} /> Tambah Nomor
            </button>
          </div>

          <button onClick={save} disabled={saving} data-testid="owner-profile-save"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Simpan Perubahan
          </button>
        </div>
      )}
    </OwnerLayout>
  );
}
